import {
  DepartmentsEnum,
  FilterSource,
} from '@moonpig/web-explore-types-graphql'
import { GALLERY_PAGE_SIZE, PARENT_DEPARTMENTS } from '../../constants'
import { mapProduct } from '../helpers/mapper'
import { runGraphQLQuery } from '../helpers/runGraphQLQuery'
import { AppContext } from '../types/context'
import {
  DepartmentCount,
  SearchProduct,
  SearchRequest,
  SearchRequestContext,
  SearchRequestResponse,
  SearchSortOrder,
} from '../types/services'
import { extractDepartmentCounts } from './extractDepartmentCounts'
import { transformFiltersToGroupedFilters } from '../helpers/groupFilters'
import { ProductSearchGQL } from './query'
import {
  SearchServiceProductSearchQuery,
  SearchServiceProductSearchQueryVariables,
} from './__generated__/query'

const getHasAugmentedReality = (search: string): boolean => {
  let hasAugmentedReality = false

  // Temporary measure - video flow currently using the hasAugmentedReality field
  const videoExperienceSearchTerm = 'video-experience'

  hasAugmentedReality = !!search && search.includes(videoExperienceSearchTerm)

  return hasAugmentedReality
}

const addIndexes = (
  products: Omit<SearchProduct, 'productIndex'>[],
  startIndex: number,
) => {
  return products.map((x, i) => {
    return { ...x, productIndex: startIndex + i }
  })
}

const search: (
  context: AppContext,
  searchRequest: SearchRequestContext,
) => Promise<SearchRequestResponse> = async (
  { client, features, options },
  {
    searchTerm,
    page,
    filters,
    integerFilter,
    departments,
    sponsoredProducts,
    promotionId,
    sortOrder,
    isInitialQuery = false,
  },
) => {
  const [secondDepartment, thirdDepartment] = PARENT_DEPARTMENTS.filter(
    parentDepartment => !departments.includes(parentDepartment),
  ).map(x => [x])

  const { showCollageMaker, cardVariants, experimentValues } = features
  const {
    payPerClickFiltering,
    defaultSortOrder,
    shouldReturnSuggestions,
    shouldSearchOnAllParentDepartments,
    platform,
  } = options

  const isSmartDepartmentRedirectionExperiment = experimentValues.includes(
    'search-use-smart-department-redirection=enabled',
  )

  const filtersFor = (
    queryDepartments: DepartmentsEnum[],
    applyAllFilters = true,
  ) => {
    const appliedFilters = applyAllFilters
      ? filters
      : filters.filter(x => x.suggestion === true)

    const suggestionKeys = appliedFilters
      .filter(x => x.suggestion)
      .map(x => x.key)

    const groupedFilters = transformFiltersToGroupedFilters(appliedFilters)

    const finalGroupedFilters = isSmartDepartmentRedirectionExperiment
      ? groupedFilters.map(group => ({
          ...group,
          keysWithMetadata: group.keys.map(facetKey => ({
            facetKey,
            source: suggestionKeys.includes(facetKey)
              ? FilterSource.SUGGESTION
              : FilterSource.FILTER,
          })),
        }))
      : groupedFilters

    return {
      hasAugmentedReality: getHasAugmentedReality(searchTerm),
      ...(payPerClickFiltering && { onlyPPCResults: payPerClickFiltering }),
      ...(promotionId && { promotionId }),
      ...(showCollageMaker && { hasCollageMaker: true }),
      groupedFacets: finalGroupedFilters,
      ...(integerFilter && { integerFilter }),
      ...(((queryDepartments?.includes('ALL_CARDS' as DepartmentsEnum) ??
        false) ||
        queryDepartments?.includes('GREETING_CARDS' as DepartmentsEnum) ||
        false) && {
        variants: cardVariants,
      }),
    }
  }
  const offset = page * GALLERY_PAGE_SIZE - GALLERY_PAGE_SIZE

  const result = await runGraphQLQuery<
    SearchServiceProductSearchQuery,
    SearchServiceProductSearchQueryVariables,
    {
      products: SearchProduct[]
      suggestions: SearchProduct[]
      totalCount: number
      departments: DepartmentCount[]
      supportedSortOrders: SearchSortOrder[]
      totalClickRankDocumentCount: number
    }
  >({
    client,
    query: ProductSearchGQL,
    variables: {
      searchTerm,
      offset,
      limit: GALLERY_PAGE_SIZE,
      department: departments,
      filters: filtersFor(departments),
      sponsoredProducts,
      returnSuggestions: shouldReturnSuggestions && page === 1,
      searchOnAllParentDepartments:
        shouldSearchOnAllParentDepartments &&
        page === 1 &&
        !isSmartDepartmentRedirectionExperiment,
      secondDepartment: secondDepartment || [],
      secondProductSearchFilters: filtersFor(secondDepartment, false),
      thirdDepartment: thirdDepartment || [],
      thirdProductSearchFilters: filtersFor(thirdDepartment, false),
      sortOrder: sortOrder || defaultSortOrder,
      experimentValues,
      platform,
      nbaAlgorithm: undefined,
      isInitialQuery,
    },
    extractResult: data => {
      const products = data.productSearch.products.map(product =>
        mapProduct(product, sponsoredProducts),
      )
      const suggestions =
        data.productSearch.suggestions?.map(product =>
          mapProduct(product, sponsoredProducts),
        ) || []

      const alternativeDepartmentCounts: DepartmentCount[] =
        isSmartDepartmentRedirectionExperiment
          ? data.productSearch.departmentCounts.map(x => ({
              department: x.department,
              count: x.count,
            }))
          : extractDepartmentCounts({
              departments: [
                ...(secondDepartment || []),
                ...(thirdDepartment ?? []),
              ],
              data,
            })

      const departmentCounts = isSmartDepartmentRedirectionExperiment
        ? alternativeDepartmentCounts
        : [
            ...alternativeDepartmentCounts,
            {
              department: departments[0],
              count: data.productSearch.count,
            },
          ]

      return {
        products: addIndexes(products, offset),
        suggestions: addIndexes(suggestions, 0),
        totalCount: data.productSearch.count,
        spellingCorrection:
          data?.productSearch?.spellingCorrection || undefined,
        departments: departmentCounts,
        supportedSortOrders: data.productSearch.supportedSortOrders,
        totalClickRankDocumentCount:
          data.productSearch.totalClickRankDocumentCount,
        redirectedDepartment: data.productSearch.redirectedDepartment,
        appliedIntents: data.productSearch.appliedIntents,
      }
    },
  })

  return {
    ...result,
    page,
  }
}

export const createSearch = (context: AppContext): SearchRequest => {
  return async request => {
    return search(context, request)
  }
}
